import { useState, useCallback, useRef, createContext, useContext, ReactNode, createElement } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getListFilesQueryKey,
  getGetRecentFilesQueryKey,
  getGetStarredFilesQueryKey,
  getGetTrashFilesQueryKey,
  getGetStorageStatsQueryKey,
  getSearchFilesQueryKey,
} from "@workspace/api-client-react";
import { toast } from "@/hooks/use-toast";
export interface UploadItem {
  id: string;
  fileName: string;
  fileSize: number;
  progress: number;
  speed: number;
  status: "uploading" | "done" | "error" | "cancelled";
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 Б";
  const k = 1024;
  const sizes = ["Б", "КБ", "МБ", "ГБ"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

export function formatSpeed(bytesPerSec: number): string {
  if (bytesPerSec <= 0) return "";
  const k = 1024;
  if (bytesPerSec < k) return `${Math.round(bytesPerSec)} Б/с`;
  if (bytesPerSec < k * k) return `${(bytesPerSec / k).toFixed(1)} КБ/с`;
  return `${(bytesPerSec / (k * k)).toFixed(1)} МБ/с`;
}

export { formatBytes as formatUploadBytes };

interface UploadContextValue {
  uploads: UploadItem[];
  uploadFiles: (files: File[], parentId?: string) => void;
  uploadWithProgress: (file: File, parentId?: string) => void;
  cancelUpload: (uploadId: string) => void;
  cancelAllUploads: () => void;
  totalProgress: number;
  isUploading: boolean;
}

const UploadContext = createContext<UploadContextValue | null>(null);

export function UploadProvider({ children }: { children: ReactNode }) {
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const queryClient = useQueryClient();

  const speedTracker = useRef<Map<string, { lastLoaded: number; lastTime: number }>>(new Map());
  const xhrMap = useRef<Map<string, XMLHttpRequest>>(new Map());
  const invalidateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingUploadsRef = useRef(0);

  const scheduleInvalidation = useCallback(() => {
    if (invalidateTimer.current) clearTimeout(invalidateTimer.current);
    const delay = pendingUploadsRef.current > 0 ? 2000 : 500;
    invalidateTimer.current = setTimeout(() => {
      invalidateTimer.current = null;
      queryClient.invalidateQueries({ queryKey: getListFilesQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetRecentFilesQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetStorageStatsQueryKey() });
      queryClient.invalidateQueries({ queryKey: ["list-files"] });
      queryClient.invalidateQueries({ queryKey: ["storage-stats"] });
    }, delay);
  }, [queryClient]);

  const cancelUpload = useCallback((uploadId: string) => {
    const xhr = xhrMap.current.get(uploadId);
    if (xhr) {
      xhr.abort();
      xhrMap.current.delete(uploadId);
      pendingUploadsRef.current = Math.max(0, pendingUploadsRef.current - 1);
    }
    speedTracker.current.delete(uploadId);
    setUploads(prev => prev.map(u =>
      u.id === uploadId && u.status === "uploading"
        ? { ...u, progress: 0, speed: 0, status: "cancelled" as const }
        : u
    ));
    setTimeout(() => {
      setUploads(prev => prev.filter(u => u.id !== uploadId));
    }, 1500);
  }, []);

  const cancelAllUploads = useCallback(() => {
    xhrMap.current.forEach((xhr, id) => {
      xhr.abort();
      speedTracker.current.delete(id);
    });
    xhrMap.current.clear();
    pendingUploadsRef.current = 0;
    setUploads(prev => prev.map(u =>
      u.status === "uploading"
        ? { ...u, progress: 0, speed: 0, status: "cancelled" as const }
        : u
    ));
    setTimeout(() => {
      setUploads(prev => prev.filter(u => u.status !== "cancelled"));
    }, 1500);
  }, []);

  const uploadWithProgress = useCallback((file: File, parentId?: string) => {
    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    pendingUploadsRef.current++;

    speedTracker.current.set(uploadId, { lastLoaded: 0, lastTime: Date.now() });

    setUploads(prev => [...prev, {
      id: uploadId,
      fileName: file.name,
      fileSize: file.size,
      progress: 0,
      speed: 0,
      status: "uploading",
    }]);

    const xhr = new XMLHttpRequest();
    xhrMap.current.set(uploadId, xhr);
    const formData = new FormData();
    formData.append("file", file);
    if (parentId) formData.append("parentId", parentId);

    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) {
        const pct = Math.round((e.loaded / e.total) * 100);
        const now = Date.now();
        const tracker = speedTracker.current.get(uploadId);
        let speed = 0;
        if (tracker) {
          const elapsed = (now - tracker.lastTime) / 1000;
          if (elapsed > 0.3) {
            const byteDiff = e.loaded - tracker.lastLoaded;
            speed = byteDiff / elapsed;
            speedTracker.current.set(uploadId, { lastLoaded: e.loaded, lastTime: now });
          } else {
            speed = -1;
          }
        }
        setUploads(prev => prev.map(u =>
          u.id === uploadId
            ? { ...u, progress: pct, ...(speed >= 0 ? { speed } : {}) }
            : u
        ));
      }
    });

    xhr.addEventListener("load", () => {
      speedTracker.current.delete(uploadId);
      xhrMap.current.delete(uploadId);
      pendingUploadsRef.current = Math.max(0, pendingUploadsRef.current - 1);
      if (xhr.status >= 200 && xhr.status < 300) {
        setUploads(prev => prev.map(u =>
          u.id === uploadId ? { ...u, progress: 100, speed: 0, status: "done" } : u
        ));
        scheduleInvalidation();
        setTimeout(() => {
          setUploads(prev => prev.filter(u => u.id !== uploadId));
        }, 2000);
      } else {
        setUploads(prev => prev.map(u =>
          u.id === uploadId ? { ...u, speed: 0, status: "error" } : u
        ));
        try {
          const body = JSON.parse(xhr.responseText);
          if (body.error === "QUOTA_EXCEEDED") {
            toast({
              variant: "destructive",
              title: "Недостаточно места",
              description: body.message || "Ваше хранилище заполнено. Удалите файлы или перейдите на другой тариф.",
              action: createElement("a", {
                href: "/plans",
                style: {
                  padding: "6px 12px",
                  borderRadius: "6px",
                  background: "rgba(0,240,255,0.15)",
                  border: "1px solid rgba(0,240,255,0.3)",
                  color: "#00F0FF",
                  fontSize: "12px",
                  fontWeight: 600,
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                },
              }, "Сменить тариф") as any,
            });
          } else if (body.error === "PLAN_OVERDUE") {
            toast({
              variant: "destructive",
              title: "Тариф просрочен",
              description: "Оплатите тариф, чтобы продолжить загрузку файлов.",
              action: createElement("a", {
                href: "/plans",
                style: {
                  padding: "6px 12px",
                  borderRadius: "6px",
                  background: "rgba(0,240,255,0.15)",
                  border: "1px solid rgba(0,240,255,0.3)",
                  color: "#00F0FF",
                  fontSize: "12px",
                  fontWeight: 600,
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                },
              }, "Оплатить") as any,
            });
          } else {
            toast({
              variant: "destructive",
              title: "Ошибка загрузки",
              description: body.message || `Не удалось загрузить «${file.name}»`,
            });
          }
        } catch {
          toast({
            variant: "destructive",
            title: "Ошибка загрузки",
            description: `Не удалось загрузить «${file.name}». Код ответа: ${xhr.status}`,
          });
        }
        setTimeout(() => {
          setUploads(prev => prev.filter(u => u.id !== uploadId));
        }, 5000);
      }
    });

    xhr.addEventListener("error", () => {
      speedTracker.current.delete(uploadId);
      xhrMap.current.delete(uploadId);
      pendingUploadsRef.current = Math.max(0, pendingUploadsRef.current - 1);
      setUploads(prev => prev.map(u =>
        u.id === uploadId ? { ...u, speed: 0, status: "error" } : u
      ));
      toast({
        variant: "destructive",
        title: "Ошибка сети",
        description: `Не удалось загрузить «${file.name}». Проверьте подключение к Интернету.`,
      });
      setTimeout(() => {
        setUploads(prev => prev.filter(u => u.id !== uploadId));
      }, 5000);
    });

    xhr.addEventListener("abort", () => {
      speedTracker.current.delete(uploadId);
      xhrMap.current.delete(uploadId);
    });
    xhr.open("POST", "/api/files/upload");
    xhr.withCredentials = true;
    xhr.send(formData);
  }, [scheduleInvalidation]);

  const uploadFiles = useCallback(async (files: File[], parentId?: string) => {
    const totalSize = files.reduce((s, f) => s + f.size, 0);
    try {
      const resp = await fetch("/api/storage/stats", {
        credentials: "include",
      });
      if (resp.ok) {
        const stats = await resp.json();
        const free = (stats.totalBytes || 0) - (stats.usedBytes || 0);
        if (totalSize > free) {
          const fmtFree = formatBytes(Math.max(0, free));
          const fmtNeed = formatBytes(totalSize);
          toast({
            variant: "destructive",
            title: "Недостаточно места",
            description: `Нужно ${fmtNeed}, свободно ${fmtFree}. Удалите файлы или смените тариф.`,
            action: createElement("a", {
              href: "/plans",
              style: {
                padding: "6px 12px",
                borderRadius: "6px",
                background: "rgba(0,240,255,0.15)",
                border: "1px solid rgba(0,240,255,0.3)",
                color: "#00F0FF",
                fontSize: "12px",
                fontWeight: 600,
                textDecoration: "none",
                whiteSpace: "nowrap",
              },
            }, "Сменить тариф") as any,
          });
          return;
        }
      }
    } catch {}
    files.forEach(f => uploadWithProgress(f, parentId));
  }, [uploadWithProgress]);

  const totalProgress = uploads.length > 0
    ? Math.round(uploads.reduce((sum, u) => sum + u.progress, 0) / uploads.length)
    : 0;

  const isUploading = uploads.some(u => u.status === "uploading");

  return createElement(UploadContext.Provider, {
    value: { uploads, uploadFiles, uploadWithProgress, cancelUpload, cancelAllUploads, totalProgress, isUploading },
  }, children);
}

export function useUploadProgress(): UploadContextValue {
  const ctx = useContext(UploadContext);
  if (!ctx) {
    throw new Error("useUploadProgress must be used within UploadProvider");
  }
  return ctx;
}
